import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  ChoiceRadio,
  ChoiceRadioItem,
} from '@design-system';

import { Loader2 } from 'lucide-react';

import { useEffect, useState } from 'react';

import type { API } from '@/api/types';

type DisconnectScope = API.DistributionPartnerDisconnectScope;

interface PartnerDisconnectDialogProps {
  open: boolean;
  contactName: string;
  isDisconnecting: boolean;
  onConfirm: (scope: DisconnectScope) => void;
  onCancel: () => void;
}

const OPTIONS: { value: DisconnectScope; title: string; description: (name: string) => string }[] =
  [
    {
      value: 'future',
      title: 'Stop sharing future clients only',
      description: name =>
        `${name} keeps access to the clients you've already shared, but new clients you add won't be shared with them.`,
    },
    {
      value: 'all',
      title: 'Remove all sharing',
      description: name =>
        `${name} loses access to every client you've shared with them, including ones shared earlier, and the connection is removed.`,
    },
  ];

export function PartnerDisconnectDialog({
  open,
  contactName,
  isDisconnecting,
  onConfirm,
  onCancel,
}: PartnerDisconnectDialogProps) {
  const [scope, setScope] = useState<DisconnectScope | null>(null);

  useEffect(() => {
    if (open) setScope(null);
  }, [open]);

  return (
    <AlertDialog
      open={open}
      onOpenChange={isOpen => !isOpen && !isDisconnecting && onCancel()}
    >
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-primary-dark">
            Stop sharing clients with {contactName}?
          </AlertDialogTitle>
          <AlertDialogDescription className="text-foreground">
            Choose how much of your sharing with this contact you'd like to remove.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <ChoiceRadio
          value={scope ?? ''}
          onValueChange={value => setScope(value as DisconnectScope)}
        >
          {OPTIONS.map(option => (
            <ChoiceRadioItem
              key={option.value}
              value={option.value}
              title={option.title}
              description={option.description(contactName)}
            />
          ))}
        </ChoiceRadio>

        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isDisconnecting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => scope && onConfirm(scope)}
            disabled={!scope || isDisconnecting}
          >
            {isDisconnecting ? (
              <>
                <Loader2 className="animate-spin" />
                Removing…
              </>
            ) : (
              'Remove sharing'
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
